<template>
  <div>
    <div class="page-wrapper-table">
      <h3>{{ $t("ELIXIO_CONNECT.SHIPMENTS_LIST") }}</h3>
      <div class="page-wrapper-table-header">
        <el-select
          class="select-primary pagination-select"
          v-model="pagination.perPage"
          :placeholder="$t('ELIXIO_CONNECT.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>
        <organization-selector
          v-if="$currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)"
          @organizationChanged="
            (organizationId) => {
              selectedOrganization = organizationId;
            }
          "
        />
        <base-input>
          <el-select
            :label="$t('ELIXIO_CONNECT.STATUS')"
            :placeholder="$t('ELIXIO_CONNECT.STATUS')"
            v-model="selectedStatut"
            @change="
              (status) => {
                selectedStatut = status;
              }
            "
          >
            <el-option
              v-for="(value, key) in SHIPMENT_STATUS"
              :key="key"
              :value="value"
              :label="
                value ? $t(`ELIXIO_CONNECT.SHIPMENT_STATUS_${value}`) : ''
              "
            />
          </el-select>
        </base-input>
        <base-input>
          <el-select
            :label="$t('COMMON.TYPE')"
            :placeholder="$t('COMMON.TYPE')"
            v-model="selectedType"
            @change="
              (type) => {
                selectedType = type;
              }
            "
          >
            <el-option
              v-for="(value, key) in PACKAGING_TYPES"
              :key="key"
              :value="value"
              :label="value ? $t(`ELIXIO_CONNECT.${value}`) : ''"
            />
          </el-select>
        </base-input>
        <base-button
          class="btn base-button refresh btn-icon btn-fab btn-info"
          icon
          size="sm"
          @click="resetFilters"
        >
          <span class="btn-inner--icon">
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.666016 3.46648V7.46648M0.666016 7.46648H4.66602M0.666016 7.46648L3.75935 4.55981C4.47585 3.84296 5.36226 3.31929 6.33589 3.03766C7.30951 2.75604 8.33861 2.72565 9.32716 2.94932C10.3157 3.17299 11.2315 3.64344 11.989 4.31676C12.7466 4.99008 13.3212 5.84434 13.6593 6.79981M15.3327 14.1331V10.1331M15.3327 10.1331H11.3327M15.3327 10.1331L12.2393 13.0398C11.5229 13.7567 10.6364 14.2803 9.66281 14.562C8.68919 14.8436 7.66009 14.874 6.67154 14.6503C5.68299 14.4266 4.76722 13.9562 4.00966 13.2829C3.2521 12.6095 2.67746 11.7553 2.33935 10.7998"
                stroke="white"
                stroke-width="1.33333"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </base-button>
        <base-input
          v-model="query"
          type="search"
          class="w-100"
          prepend-icon="far fa-search"
          :placeholder="$t('ELIXIO_CONNECT.SEARCH')"
          clearable
        />
      </div>
      <div class="page-wrapper-table-body">
        <div class="page-wrapper-table-body-inner">
          <el-table
            class="align-items-center table-flush"
            header-row-class-name="thead-light"
            :data="shipments"
            @sort-change="sortChange"
          >
            <div class="table-loading" slot="empty" v-if="loading">
              <img src="/img/loading.gif" />
            </div>
            <el-table-column
              :label="$t('ELIXIO_CONNECT.NUMERO')"
              prop="code"
              sortable="custom"
              min-width="100"
            >
              <template v-slot="{ row }">
                <span>
                  {{ row.id ?? "" }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('COMMON.NAME')"
              prop="code"
              sortable="custom"
              min-width="300"
            >
              <template v-slot="{ row }">
                <span>
                  {{ row.name ?? "" }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('ELIXIO_CONNECT.STATUS')"
              prop="status"
              min-width="200"
            >
              <template v-slot="{ row }">
                <status-badge :status="row.status" />
              </template>
            </el-table-column>

            <el-table-column
              label="Tracking"
              prop="tracking_number"
              width="160"
            />

            <el-table-column label="Destinataire" min-width="180">
              <template #default="{ row }">
                <div>
                  <strong>{{ row.recipient?.company_name }}</strong>
                  <div class="text-xs text-gray-500">
                    {{ row.recipient?.contact_name }}
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="Ramassage" width="180">
              <template #default="{ row }">
                <div v-if="row.pickup_data">
                  {{ formatTime(row.pickup_data.ready_at) }} →
                  {{ formatTime(row.pickup_data.ready_until) }}
                </div>
                <div v-else class="text-gray-400">Aucun</div>
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('COMMON.ORGANIZATION')"
              prop="status"
              min-width="220"
            >
              <template v-slot="{ row }">
                <span>{{ getHolderName(row.service.holder) }}</span>
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('ELIXIO_CONNECT.SHIPMENT_CREATED_AT')"
              prop="created_at"
              sortable="custom"
              min-width="220"
            >
              <template v-slot="{ row }">
                {{ $formatDate(row.created_at) }}
              </template>
            </el-table-column>

            <el-table-column fixed="right" min-width="120">
              <div slot-scope="{ row }" class="table-actions">
                <el-tooltip
                  :content="$t('COMMON.VIEW')"
                  placement="top"
                  v-if="$currentUserCan($permissions.PERM_VIEW_FILES)"
                >
                  <a
                    type="text"
                    @click="viewShipment(row)"
                    class="table-action"
                    data-toggle="tooltip"
                    style="cursor: pointer"
                  >
                    <img src="/img/expand-icon.svg" alt="icon" />
                  </a>
                </el-tooltip>
              </div>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div slot="footer" class="page-wrapper-table-footer">
        <p class="card-category">
          {{
            $t("ELIXIO_CONNECT.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", {
              from: total ? from + 1 : 0,
              to: to,
              of: total,
            })
          }}
          <span v-if="selectedRows.length">
            &nbsp; &nbsp;
            {{
              $t("ELIXIO_CONNECT.X_LINES_SELECTED", {
                count: selectedRows.length,
              })
            }}
          </span>
        </p>
        <base-pagination
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        />
      </div>
    </div>
    <el-drawer
      :title="$t('ELIXIO_CONNECT.SHIPMENT')"
      :visible.sync="showShipmentDetails"
      direction="rtl"
      size="50%"
      append-to-body
    >
      <div class="shipment-details px-5">
        <view-shipment-component
          :shipment="currentShipment"
          @shipmentStatusUpdated="refreshData($event)"
        />
      </div>
    </el-drawer>
  </div>
</template>
<script>
import { Tag, Row, Col, Drawer } from "element-ui";
import _ from "lodash";
import requestErrorMixin from "@/mixins/request-error-mixin";
import BasePagination from "../components/atoms/BasePagination.vue";
import { POSSIBLE_HOLDER_ATTRIBUTES } from "../constants/common";
import {
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_DRAFT,
  PACKAGING_TYPES,
} from "../constants/shipment";
import StatusBadge from "../components/atoms/StatusBadge.vue";
import ViewShipmentComponent from "./ViewShipmentComponent.vue";
import OrganizationSelector from "@/components/OrganizationSelector.vue";

export default {
  name: "ShipmentsList",

  components: {
    BasePagination,
    StatusBadge,
    ViewShipmentComponent,
    OrganizationSelector,
    [Col.name]: Col,
    [Row.name]: Row,
    [Tag.name]: Tag,
    [Drawer.name]: Drawer,
  },

  mixins: [requestErrorMixin],

  data() {
    return {
      query: null,
      selectedRows: [],
      sort: "-created_at",
      total: 0,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100, 500],
      },
      shipments: [],
      loading: true,
      selectedStatut: null,
      selectedType: null,
      selectedOrganization: null,
      SHIPMENT_STATUS: SHIPMENT_STATUS,
      PACKAGING_TYPES: PACKAGING_TYPES,
      SHIPMENT_STATUS_DRAFT: SHIPMENT_STATUS_DRAFT,
      currentShipmentId: null,
      currentShipment: {},
      showShipmentDetails: false,
      POSSIBLE_HOLDER_ATTRIBUTES: POSSIBLE_HOLDER_ATTRIBUTES,
    };
  },

  computed: {
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },

    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
  },

  watch: {
    query: {
      handler: "getListDebounced",
      immediate: true,
    },
    pagination: {
      handler: "getList",
      immediate: false,
      deep: true,
    },
    selectedStatut: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedType: {
      handler: "getListDebounced",
      immediate: true,
    },
    selectedOrganization: {
      handler: "getListDebounced",
      immediate: true,
    },
  },

  methods: {
    getHolderName(holder) {
      if (!holder) return "-";
      let name = "";
      this.POSSIBLE_HOLDER_ATTRIBUTES.forEach((attr) => {
        if (holder[attr]) {
          if (name) {
            name += " ";
          }
          name += holder[attr];
        }
      });
      return name || "-";
    },

    resetFilters() {
      this.query = null;
      this.selectedStatut = null;
      this.selectedType = null;
      this.selectedOrganization = null;
    },

    getListDebounced: _.debounce(function () {
      this.getList();
    }, 300),

    viewShipment(shipment) {
      this.currentShipmentId = shipment.id;
      this.currentShipment = shipment;
      this.showShipmentDetails = true;
    },
    refreshData(status = "draft") {
      this.$emit("shipmentStatusUpdated", status);
      this.getList();
    },

    async getList() {
      try {
        this.loading = true;
        let params = {
          sort: this.sort,
          "filter.search": this.query,
          "filter.status": this.selectedStatut,
          "filter.type": this.selectedType,
          "page.number": this.pagination.currentPage,
          "page.size": this.pagination.perPage,
        };
        if (this.selectedOrganization != null) {
          params.holder_type = "organization";
          params.holder_id = this.selectedOrganization;
        }
        const response = await this.$store.dispatch(
          "elixioConnectModule/getAllShipments",
          { params: params }
        );
        this.shipments = response.data ?? [];
        this.total = response.meta.total;
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      }
    },

    formatTime(timeObj) {
      if (!timeObj?.hour) return "";
      const h = String(timeObj.hour).padStart(2, "0");
      const m = String(timeObj.minute).padStart(2, "0");
      return `${h}:${m}`;
    },

    sortChange({ prop, order }) {
      if (order === "descending") {
        this.sort = `-${prop}`;
      } else {
        this.sort = `${prop}`;
      }
      this.getList();
    },

    refreshList() {
      this.getList();
    },
  },
  created() {
    this.getList();
  },
};
</script>
